import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Cloud, Building, Users, Star, DollarSign } from 'lucide-react';

interface EnhancedLocationContentProps {
  roofingChallenges: string;
  buildingCodes: string;
  weatherConsiderations: string;
  localServices: string;
  communityInfo: string;
  testimonials: string;
  pricingFactors: string;
  cityName: string;
}

const EnhancedLocationContent: React.FC<EnhancedLocationContentProps> = ({
  roofingChallenges,
  buildingCodes,
  weatherConsiderations,
  localServices,
  communityInfo,
  testimonials,
  pricingFactors,
  cityName
}) => {
  return (
    <div className="space-y-8">
      {/* Local Roofing Challenges */}
      <Card className="border-brand-trophy-gold/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-6 w-6 text-brand-trophy-gold" />
            {cityName} Roofing Challenges & Solutions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground leading-relaxed">{roofingChallenges}</p>
        </CardContent>
      </Card>

      {/* Building Codes & Permits */}
      <Card className="border-brand-safety-orange/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="h-6 w-6 text-brand-safety-orange" />
            Building Codes & Permits in {cityName}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="prose prose-sm max-w-none">
            <div className="whitespace-pre-line text-muted-foreground leading-relaxed">
              {buildingCodes}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Weather Considerations */}
      <Card className="border-blue-500/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cloud className="h-6 w-6 text-blue-500" />
            Weather & Climate Considerations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="whitespace-pre-line text-muted-foreground leading-relaxed">
            {weatherConsiderations}
          </div>
        </CardContent>
      </Card>

      {/* Local Services */}
      <Card className="border-green-500/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-6 w-6 text-green-500" />
            Our {cityName} Services
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="whitespace-pre-line text-muted-foreground leading-relaxed">
            {localServices}
          </div>
        </CardContent>
      </Card>

      {/* Community Information */}
      <Card className="border-purple-500/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-6 w-6 text-purple-500" />
            About {cityName}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground leading-relaxed">{communityInfo}</p>
        </CardContent>
      </Card>

      {/* Customer Testimonials */}
      <Card className="border-yellow-500/20 bg-yellow-50/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-6 w-6 text-yellow-500" />
            What {cityName} Customers Say
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {testimonials.split('" - ').map((testimonial, index) => {
              if (index === 0) return null; // Skip first empty split
              const [quote, attribution] = testimonial.split('\n\n')[0].split(' - ');
              return (
                <div key={index} className="bg-white p-4 rounded-lg border">
                  <p className="text-muted-foreground italic mb-2">"{quote}"</p>
                  <p className="text-sm font-medium text-brand-charcoal-gray">- {attribution}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Pricing Information */}
      <Card className="border-brand-trophy-gold/20 bg-brand-trophy-gold/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-brand-trophy-gold" />
            {cityName} Roofing Investment Guide
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="whitespace-pre-line text-muted-foreground leading-relaxed">
            {pricingFactors}
          </div>
        </CardContent>
      </Card>

      {/* Call to Action */}
      <Card className="border-brand-safety-orange bg-gradient-to-r from-brand-safety-orange to-brand-safety-orange/90 text-white">
        <CardContent className="p-8 text-center">
          <h3 className="text-2xl font-bold mb-4">Ready to Get Started in {cityName}?</h3>
          <p className="text-xl mb-6 opacity-90">
            Don't wait for roofing problems to get worse. Get your free estimate today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:2523022003"
              className="bg-white text-brand-safety-orange px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Call (252) 302-2003
            </a>
            <a 
              href="/book-inspection"
              className="bg-brand-trophy-gold text-brand-charcoal-gray px-6 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-colors"
            >
              Schedule Free Inspection
            </a>
          </div>
          <p className="text-sm mt-4 opacity-75">
            ✓ Licensed & Insured • ✓ A+ BBB Rating • ✓ Local Family Business
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default EnhancedLocationContent;